import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import FileManager from "../../../filemanager/FileManager/FileManager";
import { ToastContainer, toast } from "react-toastify";
import Loading from "../../Loading/Loading";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import { useSelection } from "../../../filemanager/contexts/SelectionContext";

// --- Функции для OnlyOffice Editor ---
function onDocumentReady(event) {}

function onLoadComponentError(errorCode, errorDescription) {
  switch (errorCode) {
    case -1:
      break;
    case -2:
      break;
    case -3:
      break;
    default:
      break;
  }
  toast.error("Ошибка при загрузке редактора документов.");
}

// --- Компоненты ---

const FilePreviewer = ({ file, files, object, token, onFileChange }) => {
  const [currentFile, setCurrentFile] = useState(file);
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const { setSelectedFiles } = useSelection();

  const OFFICE_EXTENSIONS = ["docx", "xlsx", "pptx", "pdf"];
  const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif"];

  const OFFICE_DOCUMENT_TYPES = {
    docx: "word",
    xlsx: "cell",
    pptx: "slide",
    pdf: "word",
  };

  const ext = currentFile.name.split(".").pop().toLowerCase();
  const isOfficeDoc = OFFICE_EXTENSIONS.includes(ext);
  const isImage = IMAGE_EXTENSIONS.includes(ext);

  useEffect(() => {
    if (onFileChange) {
      onFileChange(currentFile);
    }
  }, [currentFile, onFileChange]);

  useEffect(() => {
    setImageUrl("");

    if (isImage) {
      setLoading(true);
      axios
        .get(
          `/api/ldoc/${object.LDoc}/workdir/preview/?path=${encodeURIComponent(
            currentFile.path
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          }
        )
        .then((res) => {
          const url = URL.createObjectURL(new Blob([res.data]));
          setImageUrl(url);
          setLoading(false);
        })
        .catch(() => {
          toast.error(`Невозможно просмотреть "${currentFile.name}"`);
          setLoading(false);
        });
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [currentFile, object.LDoc, token, isImage]);

  const idx = files.findIndex((f) => f.path === currentFile.path);
  
  const prev = () => {
    if (files[idx - 1]) {
      const newFile = files[idx - 1];
      setCurrentFile(newFile);
      setSelectedFiles([newFile]);
    }
  };

  const next = () => {
    if (files[idx + 1]) {
      const newFile = files[idx + 1];
      setCurrentFile(newFile);
      setSelectedFiles([newFile]);
    }
  };

  const serverGetString = useMemo(() => {
    const cacheBuster = Date.now();
    return `&parentOrigin=https://lk.soyzstal.ru&v=${cacheBuster}`;
  }, []);

  let docEditorConfig = {};
  if (isOfficeDoc) {
    const encodedPath = encodeURIComponent(currentFile.path);
    const randomString = Math.random().toString(36).substring(2, 8);
    const documentUrl = `https://lk.soyzstal.ru/servedocs/${token}/${object.LDoc}?path=${encodedPath}&v=${randomString}`;

    docEditorConfig = {
      document: {
        fileType: ext,
        key: `${object.LDoc}_${currentFile.path}`.replace(
          /[^a-zA-Z0-9_.-]/g,
          "_"
        ),
        title: currentFile.name,
        url: documentUrl,
        permissions: {
          download: false,
          print: false,
          edit: false,
          review: false,
          copy: false,
          modifyContentControl: false,
          modifyFilter: false,
          comment: true,
          reviewMode: "final",
        },
      },
      documentType: OFFICE_DOCUMENT_TYPES[ext],
      editorConfig: {
        lang: "ru-RU",
        mode: "view",
        trackChanges: false,
        reviewMode: "final",
        showComments: true,
        trackChanges: false,
        trackChangesState: "off",
        showReviewChangesOnly: false,
        toolbarNoTabs: true,
        hideRightMenu: true,
        hideLeftMenu: true,
        hideTopToolbar: false,
      },
    };
  }

  return (
    <div
      className="prev-content"
      style={{
        textAlign: "center",
        height: "85vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button
        className="left-btn-prev"
        onClick={prev}
        disabled={idx <= 0}
        style={{ marginRight: 8, height: "50px" }}
      >
        ←
      </button>

      <div style={{ flex: 1, height: "100%", border: "1px solid #ddd" }}>
        {loading && <Loading name="Открытие файла" />}

        {!loading && (
          <>
            {isOfficeDoc ? (
              <DocumentEditor
                id="docxEditor"
                documentServerUrl="https://editoroffice.appstructura.ru/"
                documentServerget={serverGetString}
                config={docEditorConfig}
                events_onDocumentReady={onDocumentReady}
                onLoadComponentError={onLoadComponentError}
                height="100%"
              />
            ) : isImage ? (
              <img
                src={imageUrl}
                alt={currentFile.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div>Невозможно просмотреть "{currentFile.name}"</div>
            )}
          </>
        )}
      </div>

      <button
        className="right-btn-prev"
        onClick={next}
        disabled={idx === -1 || idx >= files.length - 1}
        style={{ marginLeft: 8, height: "50px" }}
      >
        →
      </button>
    </div>
  );
};

// ВЫНОСИМ ДИАЛОГ В ОТДЕЛЬНЫЙ КОМПОНЕНТ
const CreateFolderDialog = React.memo(({ 
  isOpen, 
  folderName, 
  currentPathForCreate, 
  onClose, 
  onFolderNameChange, 
  onCreate 
}) => {
  const inputRef = useRef(null);

  // Фокус на поле ввода при открытии
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Небольшая задержка для гарантии, что DOM обновился
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (folderName.trim()) {
      onCreate();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        minWidth: '300px',
        maxWidth: '400px'
      }}>
        <h3 style={{ marginBottom: '15px' }}>Создать папку</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>
              Имя папки:
            </label>
            <input
              ref={inputRef}
              type="text"
              value={folderName}
              onChange={(e) => onFolderNameChange(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              placeholder="Введите имя папки"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && folderName.trim()) {
                  onCreate();
                }
              }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
              Папка будет создана в: {currentPathForCreate === '/' ? '/Фото/' : currentPathForCreate}
            </small>
            <small style={{ color: '#999', display: 'block', marginTop: '5px' }}>
              * Создание разрешено только внутри основных директорий
            </small>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!folderName.trim()}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                background: folderName.trim() ? '#007bff' : '#ccc',
                color: 'white',
                cursor: folderName.trim() ? 'pointer' : 'not-allowed'
              }}
            >
              Создать
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

CreateFolderDialog.displayName = 'CreateFolderDialog';

const FilesTab = ({ object }) => {
  const [files, setFiles] = useState([]);
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [currentPathForCreate, setCurrentPathForCreate] = useState("/");
  const [hiddenPaths, setHiddenPaths] = useState([]);
  const isMounted = useRef(true);

  // Функция для загрузки скрытых путей
  const loadHiddenPaths = useCallback(async () => {
    if (!object?.LDoc || !isMounted.current) return;
    
    try {
      const response = await axios.get(
        `/api/ldoc/${object.LDoc}/workdir/hidden/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data && response.data.hidden_paths) {
        const paths = response.data.hidden_paths.map(h => h.path);
        setHiddenPaths(paths);
      }
    } catch (error) {
      console.error('Ошибка загрузки скрытых путей:', error);
    }
  }, [object?.LDoc, token]);

  // Функция для загрузки файлов
  const loadFiles = useCallback(async (forceReload = false) => {
    if (!object?.LDoc || !isMounted.current) return;

    setLoading(true);
    try {
      const response = await axios.get(`/api/ldoc/${object.LDoc}/workdir/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const serverFiles = response.data || [];
      setFiles(serverFiles);
      
    } catch (err) {
      console.error("Ошибка при получении списка файлов:", err);
      toast.error("Ошибка при получении списка файлов.");
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [object?.LDoc, token]);

  // Функция для создания папки
  const handleCreateFolder = useCallback(async (folderName, parentPath = "/") => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        `/api/ldoc/${object.LDoc}/workdir/create_folder/`,
        {
          folder_name: folderName,
          path: parentPath
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data) {
        toast.success("Папка успешно создана");
        await loadFiles(true);
        window.dispatchEvent(new CustomEvent('folderCreated'));
        return true;
      }
    } catch (error) {
      console.error("Ошибка создания папки:", error);
      toast.error(error.response?.data?.error || "Ошибка создания папки");
      return false;
    } finally {
      setLoading(false);
    }
  }, [object?.LDoc, token, loadFiles]);

  // Функция для скрытия файла/папки - БЕЗ ПОДТВЕРЖДЕНИЯ
  const handleHideItem = async (selectedFilesArray) => {
    try {
      setLoading(true);
      
      // Если нет выбранных файлов, выходим
      if (!selectedFilesArray || selectedFilesArray.length === 0) {
        toast.error("Не выбраны файлы для скрытия");
        return false;
      }
      
      // Скрываем все выбранные файлы
      const results = [];
      
      for (const file of selectedFilesArray) {
        if (!file.path) {
          console.error('File path is undefined!', file);
          continue;
        }
        
        try {
          const response = await axios.post(
            `/api/ldoc/${object.LDoc}/workdir/hidden/`,
            { path: file.path },
            {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            }
          );
          
          results.push({
            file: file.name,
            success: true,
            data: response.data
          });
          
        } catch (error) {
          console.error(`Error hiding file ${file.name}:`, error);
          results.push({
            file: file.name,
            success: false,
            error: error.response?.data?.error || error.message
          });
        }
      }
      
      // Анализируем результаты
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);
      
      if (successful.length > 0) {
        if (successful.length === 1) {
          toast.success(`"${successful[0].file}" скрыт`);
        } else {
          toast.success(`${successful.length} файлов скрыто`);
        }
      }
      
      if (failed.length > 0) {
        toast.error(`Не удалось скрыть ${failed.length} файлов`);
      }
      
      // Обновляем данные
      await loadHiddenPaths();
      await loadFiles(true);
      window.dispatchEvent(new CustomEvent('fileHidden'));
      
      return successful.length > 0;
      
    } catch (error) {
      console.error("Ошибка в обработчике скрытия:", error);
      toast.error("Ошибка при скрытии файлов");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Функция для открытия диалога создания папки
  const onCreateFolder = useCallback((path = "/") => {
    setCurrentPathForCreate(path);
    setFolderName("");
    setShowCreateFolderDialog(true);
  }, []);

  // Обработчик создания папки из диалога
  const handleCreateFromDialog = useCallback(async () => {
    if (!folderName.trim()) {
      toast.error("Введите имя папки");
      return;
    }
    
    const success = await handleCreateFolder(folderName.trim(), currentPathForCreate);
    if (success) {
      setShowCreateFolderDialog(false);
      setFolderName("");
    }
  }, [folderName, currentPathForCreate, handleCreateFolder]);

  // Обработчик закрытия диалога
  const handleCloseDialog = useCallback(() => {
    setShowCreateFolderDialog(false);
    setFolderName("");
  }, []);

  // Загрузка файлов при монтировании
  useEffect(() => {
    isMounted.current = true;
    
    const loadInitialData = async () => {
      if (object?.LDoc) {
        await loadHiddenPaths();
        await loadFiles();
      }
    };
    
    loadInitialData();
    
    return () => {
      isMounted.current = false;
    };
  }, [object?.LDoc]);

  // Слушаем события о загрузке файлов
  useEffect(() => {
    const handleFilesUploaded = () => {
      setTimeout(() => {
        loadFiles(true);
      }, 1000);
    };

    const handleFolderCreated = () => {
      setTimeout(() => {
        loadFiles(true);
      }, 500);
    };
    
    const handleFileHidden = () => {
      setTimeout(() => {
        loadFiles(true);
        loadHiddenPaths();
      }, 500);
    };

    window.addEventListener('filesUploaded', handleFilesUploaded);
    window.addEventListener('folderCreated', handleFolderCreated);
    window.addEventListener('fileHidden', handleFileHidden);

    return () => {
      window.removeEventListener('filesUploaded', handleFilesUploaded);
      window.removeEventListener('folderCreated', handleFolderCreated);
      window.removeEventListener('fileHidden', handleFileHidden);
    };
  }, []);

  const onDownload = (fileList) => {
    fileList.forEach((f) => {
      setLoading(true);
      const name = f.isDirectory ? `${f.name}.zip` : f.name;
      axios
        .get(
          `/api/ldoc/${object.LDoc}/workdir/download/?path=${encodeURIComponent(
            f.path
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          }
        )
        .then((resp) => {
          const url = URL.createObjectURL(new Blob([resp.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setLoading(false);
        })
        .catch(() => {
          toast.error(`Невозможно скачать "${f.name}"`);
          setLoading(false);
        });
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && <Loading name="Загрузка" />}
      
      {/* Используем мемоизированный диалог */}
      <CreateFolderDialog
        isOpen={showCreateFolderDialog}
        folderName={folderName}
        currentPathForCreate={currentPathForCreate}
        onClose={handleCloseDialog}
        onFolderNameChange={setFolderName}
        onCreate={handleCreateFromDialog}
      />
      
      <ToastContainer />
      <FileManager
        files={files}
        onDownload={onDownload}
        onCreateFolder={onCreateFolder}
        onDelete={handleHideItem}  // Прямой вызов без подтверждения
        language="ru"
        allowCreateFolder={true}
        allowUploadFile={false}
        allowCutCopy={false}
        allowDelete={true}
        filePreviewComponent={(file) => (
          <FilePreviewer
            file={file}
            files={files}
            object={object}
            token={token}
            onFileChange={(newFile) => {
              console.log("File changed to:", newFile.name);
            }}
          />
        )}
      />
      
      {/* Информационная панель о скрытых файлах */}
      {hiddenPaths.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px 15px',
          fontSize: '14px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 999,
          maxWidth: '300px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#6c757d' }}>
              📁 Скрыто элементов: <strong>{hiddenPaths.length}</strong>
            </span>
            <button
              onClick={() => {
                toast.info('Функция управления скрытыми файлами будет добавлена позже');
              }}
              style={{
                padding: '3px 8px',
                fontSize: '12px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                background: 'transparent',
                color: '#6c757d',
                cursor: 'pointer'
              }}
            >
              Управление
            </button>
          </div>
          <small style={{ color: '#999', display: 'block', marginTop: '5px' }}>
            Скрытые файлы видны только вам
          </small>
        </div>
      )}
    </div>
  );
};

export default FilesTab;